import { useEffect, useState } from 'react';
import type { NetworkNode, NetworkView } from '@rhwb/types';
import { callCloudRun } from './cloudRun';

/**
 * Amazing Network API calls — the same /api/network/* endpoints the mobile app
 * uses. Identity is the signed-in token server-side; only the admin override
 * view sends an email, and the API refuses that for anyone but admin/lord.
 */

export const fetchMyNetwork = () => callCloudRun<NetworkView>('/api/network/me');

export const fetchNetworkNode = (id: string) => callCloudRun<NetworkView>('/api/network/node', { id });

/** Admin/lord only: the viewed runner's network in profile override mode. */
export const fetchNetworkByEmail = (email: string) =>
  callCloudRun<NetworkView>('/api/network/node', { email });

/** Pick a referrer (`id`), or `null` with `foundSelf` for "I found RHWB myself". */
export const setReferrer = (id: string | null, foundSelf = false) =>
  callCloudRun<NetworkView>('/api/network/referrer', { id, foundSelf });

/** Name who brought you in when they aren't in RHWB. Name only — no email. */
export const setOutsideReferrer = (name: string) =>
  callCloudRun<NetworkView>('/api/network/referrer', { id: null, name });

/** Claim someone you brought in. First answer wins: fails if they already have a referrer. */
export const addRecruit = (id: string) => callCloudRun<NetworkView>('/api/network/recruits', { id });

/** Undo a recruit whose referrer is you. */
export const removeRecruit = (id: string) =>
  callCloudRun<NetworkView>('/api/network/recruits', { id, remove: true });

/** Add someone to your family (created if you have none). Fails if they're in another family. */
export const addFamily = (id: string) => callCloudRun<NetworkView>('/api/network/family', { id });

/** Take someone out of your family — yourself included, which leaves it. */
export const removeFamily = (id: string) =>
  callCloudRun<NetworkView>('/api/network/family', { id, remove: true });

/** Runners at your address who aren't in a family yet. Addresses never leave the API. */
export const fetchFamilySuggestions = async () =>
  (await callCloudRun<{ suggestions: NetworkNode[] }>('/api/network/family/suggestions')).suggestions;

/** "Not family": stop suggesting this runner, on every device. */
export const dismissFamilySuggestion = (id: string) =>
  callCloudRun<{ ok: true }>('/api/network/family/dismiss', { id });

export type SearchMode = 'referrer' | 'recruit' | 'family';
export const MIN_SEARCH = 2;
const SEARCH_DEBOUNCE_MS = 300;

/** Debounced name search for the picker dialogs; stale responses are dropped. */
export function useNetworkSearch(query: string, mode: SearchMode) {
  const [results, setResults] = useState<NetworkNode[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_SEARCH) {
      setResults([]);
      setSearching(false);
      return;
    }
    let stale = false;
    setSearching(true);
    const timer = setTimeout(() => {
      callCloudRun<{ results: NetworkNode[] }>('/api/network/search', { q, mode })
        .then((res) => {
          if (stale) return;
          setResults(res.results ?? []);
          setError(null);
        })
        .catch((err) => {
          if (!stale) setError(err.message);
        })
        .finally(() => {
          if (!stale) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [query, mode]);

  return { results, searching, error };
}
